import { useMemo, useState } from 'react';
import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import {
	CancelButton,
	SaveButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Modal, ModalBox } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { useDownloadedFileBlobMutation } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlobMutation';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { PlacementEditorLayout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementEditorLayout';
import { buildPlacementSlotListItems } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/signaturePlacementShared';
import { useSignaturePlacement } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/useSignaturePlacement';
import { useSignatureSlotsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useSignatureSlotsQuery';
import { useUpdateMasterDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useUpdateMasterDocument';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	templateId: string;
	fileId: string;
	contractName?: string;
	show: boolean;
	onClose: () => void;
	onSaved?: () => void;
}

const ContractPlacementModal = ({
	templateId,
	fileId,
	contractName,
	show,
	onClose,
	onSaved,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { dealId } = useRequiredParams('dealId', 'mipId');
	const { downloadedFileBlob } = useDownloadedFileBlobMutation();
	const updateMasterDocument = useUpdateMasterDocument();

	// Null until the author flips it: until then the editor follows the template's saved mode, so
	// boxes saved compact are not enlarged (and then shifted apart) on every open.
	const [showStampMetadataOverride, setShowStampMetadataOverride] = useState<boolean | null>(
		null
	);
	const { data: slotsData } = useSignatureSlotsQuery(templateId);
	const showStampMetadata = showStampMetadataOverride ?? slotsData?.showStampMetadata ?? true;

	const {
		slots,
		isLoading: slotsLoading,
		isError: slotsError,
		placementsByKey,
		slotByKey,
		selectedSlotKey,
		setSelectedSlotKey,
		onPlacementChange,
		onPlaceAtDefault,
		onRemove,
		isComplete,
		hasOverlappingPlacements,
		isDirty,
		signaturePlacements,
		datePlacementKeys,
		onAddDateField,
		datePlacements,
	} = useSignaturePlacement(templateId, showStampMetadata);

	// React Query handles the fetch lifecycle. Using useEffect + the mutation
	// here caused an infinite re-fetch loop because the mutation hook returns
	// a fresh `downloadedFileBlob` function reference every render — putting
	// it in the dep array triggered re-fire on every state change.
	const blobQuery = useQuery({
		queryKey: ['contract-placement-blob', dealId, fileId],
		queryFn: async () => {
			const data = await downloadedFileBlob({ fileId, dealId });
			return data.blob;
		},
		enabled: show && fileId.length > 0,
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: false,
	});

	const slotListItems = useMemo(
		() =>
			buildPlacementSlotListItems({
				slots,
				placementsByKey,
				datePlacementKeys,
			}),
		[slots, placementsByKey, datePlacementKeys]
	);

	const isSaving = updateMasterDocument.isPending;
	const canSave = isComplete && isDirty && !hasOverlappingPlacements && !isSaving;

	const onSave = () => {
		updateMasterDocument.mutate(
			{
				templateId,
				useManualSignaturePlacement: true,
				showStampMetadata,
				signaturePlacements,
				// No separate switch here: drawing a date box is what asks for post-dating, and
				// removing the last one withdraws it.
				requiresPostDating: datePlacements.length > 0,
				datePlacements,
			},
			{
				onSuccess: () => {
					onSaved?.();
					onClose();
				},
			}
		);
	};

	const headerTitle = contractName ? `Place signatures — ${contractName}` : 'Place signatures';

	const renderBody = () => {
		if (blobQuery.isLoading || (!blobQuery.data && !blobQuery.isError)) {
			return (
				<LoaderHost>
					<InlineLoader />
				</LoaderHost>
			);
		}
		if (blobQuery.isError || !blobQuery.data) {
			return (
				<Text size="sm" c="red">
					Failed to load the document. Please close and try again.
				</Text>
			);
		}
		if (slotsLoading) {
			return (
				<Text size="sm" c="gray.7">
					Loading signature slots…
				</Text>
			);
		}
		if (slotsError) {
			return (
				<Text size="sm" c="red">
					Failed to load signature slots.
				</Text>
			);
		}
		if (slots.length === 0) {
			return (
				<Text size="sm" c="gray.7">
					This document has no signature roles configured.
				</Text>
			);
		}
		return (
			<PlacementEditorLayout
				file={blobQuery.data}
				showMetadata={showStampMetadata}
				onShowMetadataChange={setShowStampMetadataOverride}
				placements={placementsByKey}
				slotByKey={slotByKey}
				selectedSlotKey={selectedSlotKey}
				onSelect={setSelectedSlotKey}
				onPlacementChange={onPlacementChange}
				onRemove={onRemove}
				onPlaceAtDefault={onPlaceAtDefault}
				slotListItems={slotListItems}
				placedCount={placementsByKey.size - datePlacementKeys.length}
				totalCount={slots.length}
				isComplete={isComplete}
				onBack={onClose}
				onAddDateField={onAddDateField}
				dateFieldCount={datePlacementKeys.length}
			/>
		);
	};

	const showNotice = !!blobQuery.data && !slotsLoading && !slotsError && slots.length > 0;

	return (
		<FullHeightModal isOpen={show} onClose={onClose} size="lg" closeButton={false}>
			<SectionHeader title={headerTitle} size="h4b" />
			{/* Definite height so PlacementEditorLayout's height:100% resolves and the
			    modal's own 80vh scroll never engages — the document is the only scroll,
			    and the layout keeps Cancel/Save pinned in the side-panel footer. */}
			<BodyWrapper>
				{showNotice && !isComplete ? (
					<PlacementResetNotice>
						<Text size="sm">{tMipCreate('contracts.placement.reset_notice')}</Text>
					</PlacementResetNotice>
				) : null}
				{renderBody()}
				<FooterRow>
					<CancelButton onClick={onClose} disabled={isSaving} />
					<SaveButton onClick={onSave} disabled={!canSave} />
				</FooterRow>
			</BodyWrapper>
		</FullHeightModal>
	);
};

// The placement editor needs the height: the shared Modal caps at 80vh, so lift it
// to near-full-height leaving a margin top and bottom.
const FullHeightModal = styled(Modal)`
	&& {
		${ModalBox} {
			max-height: calc(100vh - 4rem);
		}
	}
`;

const PlacementResetNotice = styled.div`
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-left: 4px solid ${({ theme }) => theme.v2.color.alternateMid.orange};
	border-radius: 4px;
	padding: 0.75rem;
	flex-shrink: 0;
`;

const BodyWrapper = styled.div`
	width: 100%;
	/* Fills the taller modal: viewport minus its 4rem margin, ModalBox's 4rem
	   padding, the section header and this wrapper's own padding-top. */
	height: calc(100vh - 12rem);
	min-height: 24rem;
	padding-top: 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

// Sits outside the scrolling panes so Cancel/Save are always reachable.
const FooterRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 0.75rem;
	padding-top: 0.75rem;
	border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	flex-shrink: 0;
`;

const LoaderHost = styled.div`
	position: relative;
	flex: 1;
	min-height: 20rem;
`;

export { ContractPlacementModal };
